/**
 * 金額（円）まわりの共通ユーティリティ。
 *
 * soremo の対価・残高などの金額は「円・整数」で扱う（小数の支払いは存在しない）。
 * 入力（`MoneyInput`）・保存・表示のすべてで本モジュールを経由させ、小数点が
 * 表示・送信されないことを一元的に担保する。
 *
 * 消費税変換は `tax.ts` に集約済みのため、利便性のためここから再エクスポートする。
 */

import { addConsumptionTax, CONSUMPTION_TAX_RATE } from "./tax";

/**
 * 任意の値を整数円（number）へ変換する。端数は四捨五入。
 * 非有限値・数値化できない値は 0 を返す。
 */
export const toYen = (raw: string | number | null | undefined): number => {
  const n = Number(raw);
  return Number.isFinite(n) ? Math.round(n) : 0;
};

/**
 * フォーム入力 value 用の整数円文字列を返す（例: `"1000"`）。
 */
export const toYenString = (
  raw: string | number | null | undefined,
): string => String(toYen(raw));

/**
 * 表示用の日本円表記（`¥1,234`）に整形する。
 * `null` / `undefined` / 非有限値は `fallback`（既定 `null`）を返す。
 */
export const formatYen = (
  amount: number | null | undefined,
  fallback: string | null = null,
): string | null => {
  if (amount === null || amount === undefined) return fallback;
  if (!Number.isFinite(amount)) return fallback;
  return `¥${toYen(amount).toLocaleString("ja-JP")}`;
};

/**
 * 符号付きの日本円表記（例: `¥1,234` / `-¥1,234`）。
 * 収支を併記するリスト（進行中合計・振込申請の支出行など）で、マイナスを
 * `¥` の前に置いて控除であることを明示したい箇所で使う。
 * 非有限値は `¥0` 相当（`fallback` 既定 `null`）を返す。
 */
export const formatYenSigned = (
  amount: number | null | undefined,
  fallback: string | null = null,
): string | null => {
  if (amount === null || amount === undefined) return fallback;
  if (!Number.isFinite(amount)) return fallback;
  const sign = amount < 0 ? "-" : "";
  return `${sign}¥${toYen(Math.abs(amount)).toLocaleString("ja-JP")}`;
};

export { addConsumptionTax, CONSUMPTION_TAX_RATE };
